import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Control,
  EmptyState,
  Input,
  Label,
  Select,
  SelectOption,
  TextArea,
  ToolJiraIcon,
  useDocumentTitle,
} from '@forge/common';
import { setRequirements, setSelectedId, useIntakeStore } from './intakeStore';
import { newId } from './intakeUtils';
import type {
  ActorRole,
  JiraIssue,
  JiraStatus,
  Requirement,
} from '@/data/mock-requirements';
import { JIRA_STATUS_META } from '@/data/mock-requirements';

type JiraType = JiraIssue['type'];

const actorNameFor = (role: ActorRole, requirement: Requirement): string => {
  if (role === 'client') return requirement.clientName;
  if (role === 'account_manager') return 'Account Manager';
  return 'Development';
};

const IntakeJiraNewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { requirements } = useIntakeStore();
  const requirement = requirements.find((r) => r.id === id);

  const actorRole =
    (location.state as { actorRole?: ActorRole } | null)?.actorRole ??
    'account_manager';

  useDocumentTitle(
    `New JIRA story · ${requirement?.reference ?? 'Intake'} · OneRing`,
  );

  const [summary, setSummary] = useState('');
  const [type, setType] = useState<JiraType>('Story');
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState<JiraStatus>('to_do');

  const canSubmit = Boolean(summary.trim());

  const goBackToDevelopment = (): void => {
    if (id) setSelectedId(id);
    navigate(`/intake/${id}`, { state: { detailTab: 'development' } });
  };

  if (!requirement) {
    return (
      <div className="p-4">
        <Card>
          <CardContent>
            <EmptyState
              icon={<ToolJiraIcon />}
              description="That request could not be found."
            />
            <div className="mt-4 flex justify-center">
              <Button variant="primary" onClick={() => navigate('/intake')}>
                Back to Intake
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCreate = (): void => {
    const now = new Date().toISOString();
    const seq = 8340 + Math.floor(Math.random() * 60);
    const key = `ONERING-${seq}`;
    const issue: JiraIssue = {
      key,
      type,
      summary: summary.trim(),
      status,
      assignee: assignee.trim() || 'Unassigned',
      url: `https://honeywell.atlassian.net/browse/${key}`,
    };
    const actor = actorNameFor(actorRole, requirement);

    setRequirements((prev) =>
      prev.map((req) =>
        req.id === requirement.id
          ? {
              ...req,
              updatedAt: now,
              jiraIssues: [...req.jiraIssues, issue],
              events: [
                ...req.events,
                {
                  id: newId(),
                  type: 'jira_updated',
                  actor,
                  actorRole,
                  createdAt: now,
                  body: `Created ${key} — ${issue.summary} (${JIRA_STATUS_META[status].label}).`,
                },
              ],
            }
          : req,
      ),
    );

    goBackToDevelopment();
  };

  return (
    <div className="space-y-4 p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'intake', label: 'Intake', href: '/intake' },
          {
            id: 'request',
            label: requirement.reference,
            href: '/intake',
          },
          { id: 'jira', label: 'New JIRA story', noAction: true },
        ]}
        onChange={({ id: crumbId }) => {
          if (crumbId === 'intake') navigate('/intake');
          if (crumbId === 'request') goBackToDevelopment();
        }}
      />

      <div className="min-w-0">
        <h2 className="text-xl font-bold">New JIRA story</h2>
        <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
          Create a JIRA story for{' '}
          <span className="font-semibold">{requirement.title}</span>. It is
          linked to this request and tracked on the Development tab. Creating a
          story does not move the request to development on its own.
        </p>
      </div>

      <Card className="max-w-3xl">
        <CardContent className="space-y-4">
          <Control hideSpacing="bottom">
            <Label htmlFor="jira-summary">Summary</Label>
            <Input
              id="jira-summary"
              placeholder="Short summary of the work"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Control>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Control hideSpacing="bottom">
              <Label htmlFor="jira-type">Type</Label>
              <Select
                id="jira-type"
                value={type}
                onChange={(e) => setType(e.target.value as JiraType)}
              >
                <SelectOption value="Story" label="Story">
                  Story
                </SelectOption>
                <SelectOption value="Task" label="Task">
                  Task
                </SelectOption>
                <SelectOption value="Bug" label="Bug">
                  Bug
                </SelectOption>
                <SelectOption value="Spike" label="Spike">
                  Spike
                </SelectOption>
              </Select>
            </Control>
            <Control hideSpacing="bottom">
              <Label htmlFor="jira-status">Status</Label>
              <Select
                id="jira-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as JiraStatus)}
              >
                <SelectOption value="to_do" label="To Do">
                  To Do
                </SelectOption>
                <SelectOption value="in_progress" label="In Progress">
                  In Progress
                </SelectOption>
                <SelectOption value="in_review" label="In Review">
                  In Review
                </SelectOption>
                <SelectOption value="done" label="Done">
                  Done
                </SelectOption>
              </Select>
            </Control>
          </div>
          <Control hideSpacing="bottom">
            <Label htmlFor="jira-assignee">Assignee</Label>
            <Input
              id="jira-assignee"
              placeholder="Leave blank for Unassigned"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            />
          </Control>
          <Control hideSpacing="bottom">
            <Label htmlFor="jira-desc">Description (optional)</Label>
            <TextArea
              id="jira-desc"
              rows={6}
              placeholder="Add any context for the development team…"
            />
          </Control>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <Button variant="secondary" onClick={goBackToDevelopment}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<ToolJiraIcon />}
              disabled={!canSubmit}
              onClick={handleCreate}
            >
              Create story
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntakeJiraNewPage;
